/**
 * BatchOnboarding — Onboard all 11 Kona-Point Shopping Mall businesses in one shot.
 *
 * Props:
 *   mallTenantId  {string}   — The mall's client_id (used in the API path)
 *   onComplete    {function} — Called with the batch result when done
 *   apiBase       {string}   — Optional API base URL override
 *
 * Features:
 *   - Pre-populated list of 11 businesses (5 existing + 6 new verticals)
 *   - Select / deselect individual businesses before submitting
 *   - Per-business progress indicator during the batch call
 *   - Summary panel with success / failure counts and per-tenant details
 *
 * Correctness Property 26:
 *   Batch onboarding creates correct number of records with partial failure
 *   resilience — failed tenants are reported but do not block the rest.
 */

import React, { useState, useCallback } from 'react';
import { API_BASE } from '../config/apiConfig';

// ── Pre-populated business list ────────────────────────────────────────────────
// 5 existing platform demos + 6 new verticals = 11 total
const DEFAULT_BUSINESSES = [
  // ── Existing 5 ──────────────────────────────────────────────────────────────
  {
    id: 'prab-restaurant',
    company_name: 'Prab-Restaurant-Hospitality',
    business_type: 'restaurant',
    contact_email: 'prab@konapoint.ai',
    capabilities: ['food_service', 'catering', 'reservations'],
    service_types: ['dine_in', 'takeout', 'catering'],
    isExisting: true,
    icon: '🍽️',
  },
  {
    id: 'greenthumb-lawn',
    company_name: 'GreenThumb-Lawn-Landscape',
    business_type: 'lawn_care',
    contact_email: 'greenthumb@konapoint.ai',
    capabilities: ['lawn_mowing', 'landscaping', 'irrigation'],
    service_types: ['scheduled_maintenance', 'one_time_service', 'consultation'],
    isExisting: true,
    icon: '🌿',
  },
  {
    id: 'coolbreeze-hvac',
    company_name: 'CoolBreeze-HVAC-Services',
    business_type: 'hvac',
    contact_email: 'coolbreeze@konapoint.ai',
    capabilities: ['hvac_repair', 'hvac_installation', 'maintenance_contracts'],
    service_types: ['emergency_repair', 'scheduled_maintenance', 'installation'],
    isExisting: true,
    icon: '❄️',
  },
  {
    id: 'safehaven-insurance',
    company_name: 'SafeHaven-Insurance-Group',
    business_type: 'insurance',
    contact_email: 'safehaven@konapoint.ai',
    capabilities: ['home_insurance', 'auto_insurance', 'business_insurance'],
    service_types: ['policy_quote', 'claims_processing', 'policy_renewal'],
    isExisting: true,
    icon: '🛡️',
  },
  {
    id: 'drainmaster-plumbing',
    company_name: 'DrainMaster-Plumbing-Network',
    business_type: 'plumbing',
    contact_email: 'drainmaster@konapoint.ai',
    capabilities: ['drain_cleaning', 'pipe_repair', 'water_heater'],
    service_types: ['emergency_repair', 'scheduled_service', 'inspection'],
    isExisting: true,
    icon: '🔧',
  },
  // ── New 6 ────────────────────────────────────────────────────────────────────
  {
    id: 'konapoint-retail',
    company_name: 'KonaPoint-Online-Retail',
    business_type: 'online_retail',
    contact_email: 'retail@konapoint.ai',
    capabilities: ['product_catalog', 'order_fulfillment', 'returns'],
    service_types: ['online_order', 'in_store_pickup', 'delivery'],
    isExisting: false,
    icon: '🛍️',
  },
  {
    id: 'konapoint-salon',
    company_name: 'KonaPoint-Salon-Studio',
    business_type: 'salon',
    contact_email: 'salon@konapoint.ai',
    capabilities: ['hair_styling', 'nail_care', 'spa_services'],
    service_types: ['appointment_booking', 'walk_in', 'package_deals'],
    isExisting: false,
    icon: '💇',
  },
  {
    id: 'konapoint-gym',
    company_name: 'KonaPoint-Fitness-Gym',
    business_type: 'gym',
    contact_email: 'gym@konapoint.ai',
    capabilities: ['fitness_training', 'group_classes', 'personal_training'],
    service_types: ['membership', 'day_pass', 'personal_training_session'],
    isExisting: false,
    icon: '🏋️',
  },
  {
    id: 'konapoint-pharmacy',
    company_name: 'KonaPoint-Pharmacy-Plus',
    business_type: 'pharmacy',
    contact_email: 'pharmacy@konapoint.ai',
    capabilities: ['prescription_filling', 'otc_products', 'health_consultations'],
    service_types: ['prescription', 'consultation', 'delivery'],
    isExisting: false,
    icon: '💊',
  },
  {
    id: 'konapoint-realty',
    company_name: 'KonaPoint-Real-Estate-Office',
    business_type: 'real_estate',
    contact_email: 'realty@konapoint.ai',
    capabilities: ['property_listings', 'buyer_representation', 'property_management'],
    service_types: ['buy', 'sell', 'rent', 'property_management'],
    isExisting: false,
    icon: '🏠',
  },
  {
    id: 'konapoint-car-insurance',
    company_name: 'KonaPoint-Car-Insurance',
    business_type: 'car_insurance',
    contact_email: 'carinsurance@konapoint.ai',
    capabilities: ['auto_insurance', 'roadside_assistance', 'claims_processing'],
    service_types: ['policy_quote', 'claims', 'renewal'],
    isExisting: false,
    icon: '🚗',
  },
];

// ── Status badge styles ────────────────────────────────────────────────────────
const STATUS_STYLES = {
  idle:       { background: '#f3f4f6', color: '#6b7280', label: 'Pending' },
  loading:    { background: '#eff6ff', color: '#3b82f6', label: 'Onboarding…' },
  success:    { background: '#f0fdf4', color: '#16a34a', label: 'Success ✓' },
  error:      { background: '#fef2f2', color: '#dc2626', label: 'Failed ✗' },
  skipped:    { background: '#fefce8', color: '#ca8a04', label: 'Skipped' },
};

const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 4px',
  },
  subtitle: {
    fontSize: '13px',
    color: '#666',
    margin: 0,
  },
  controls: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  btn: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'opacity 0.15s',
  },
  btnPrimary: {
    background: '#4f46e5',
    color: '#fff',
  },
  btnSecondary: {
    background: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
  },
  btnDanger: {
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fca5a5',
  },
  businessList: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '20px',
  },
  businessItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    transition: 'background 0.1s',
  },
  businessItemSelected: {
    background: '#f5f3ff',
  },
  businessItemDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  icon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  businessInfo: {
    flex: 1,
    minWidth: 0,
  },
  businessName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1a1a2e',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  businessMeta: {
    fontSize: '12px',
    color: '#888',
    marginTop: '2px',
  },
  existingBadge: {
    fontSize: '10px',
    background: '#eff6ff',
    color: '#3b82f6',
    padding: '2px 6px',
    borderRadius: '10px',
    fontWeight: 600,
    flexShrink: 0,
  },
  newBadge: {
    fontSize: '10px',
    background: '#f0fdf4',
    color: '#16a34a',
    padding: '2px 6px',
    borderRadius: '10px',
    fontWeight: 600,
    flexShrink: 0,
  },
  statusBadge: {
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '10px',
    fontWeight: 600,
    flexShrink: 0,
    minWidth: '80px',
    textAlign: 'center',
  },
  progressBar: {
    height: '4px',
    background: '#e5e7eb',
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  progressFill: {
    height: '100%',
    background: '#4f46e5',
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  summary: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    background: '#f9fafb',
    marginBottom: '16px',
  },
  summaryTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: '12px',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '12px',
  },
  summaryCard: {
    textAlign: 'center',
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    background: '#fff',
  },
  summaryNumber: {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 1,
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#888',
    marginTop: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  errorDetail: {
    fontSize: '12px',
    color: '#dc2626',
    background: '#fef2f2',
    padding: '8px 12px',
    borderRadius: '6px',
    marginTop: '8px',
  },
};

const BatchOnboarding = ({ mallTenantId, onComplete, apiBase }) => {
  const base = apiBase || API_BASE || '/v1';

  // Selected business IDs
  const [selected, setSelected] = useState(
    new Set(DEFAULT_BUSINESSES.map(b => b.id))
  );

  // Per-business status: idle | loading | success | error | skipped
  const [statuses, setStatuses] = useState(
    Object.fromEntries(DEFAULT_BUSINESSES.map(b => [b.id, 'idle']))
  );

  // Per-business result details (client_id, tenant_id, error message)
  const [results, setResults] = useState({});

  // Overall batch state
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [batchSummary, setBatchSummary] = useState(null);
  const [error, setError] = useState('');

  // Progress tracking
  const completedCount = Object.values(statuses).filter(
    s => s === 'success' || s === 'error' || s === 'skipped'
  ).length;
  const totalSelected = selected.size;
  const progressPct = totalSelected > 0
    ? Math.round((completedCount / totalSelected) * 100)
    : 0;

  const toggleBusiness = useCallback((id) => {
    if (running) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [running]);

  const selectAll = () => {
    if (running) return;
    setSelected(new Set(DEFAULT_BUSINESSES.map(b => b.id)));
  };

  const deselectAll = () => {
    if (running) return;
    setSelected(new Set());
  };

  const handleRun = async () => {
    if (!mallTenantId) {
      setError('Mall tenant ID is required to run batch onboarding.');
      return;
    }
    if (selected.size === 0) {
      setError('Please select at least one business to onboard.');
      return;
    }

    setError('');
    setRunning(true);
    setDone(false);
    setBatchSummary(null);

    // Reset statuses for selected businesses
    const newStatuses = { ...statuses };
    for (const id of selected) newStatuses[id] = 'idle';
    // Mark unselected as skipped
    for (const b of DEFAULT_BUSINESSES) {
      if (!selected.has(b.id)) newStatuses[b.id] = 'skipped';
    }
    setStatuses(newStatuses);

    // Build the businesses payload
    const businesses = DEFAULT_BUSINESSES
      .filter(b => selected.has(b.id))
      .map(({ id: _id, isExisting: _ie, icon: _ic, ...rest }) => rest); // eslint-disable-line no-unused-vars

    // Mark all selected as loading
    setStatuses(prev => {
      const next = { ...prev };
      for (const id of selected) next[id] = 'loading';
      return next;
    });

    try {
      const res = await fetch(`${base}/mall/${mallTenantId}/batch-onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businesses }),
      });

      const data = await res.json();

      if (!res.ok && res.status !== 207) {
        throw new Error(data.error || `Server error ${res.status}`);
      }

      // Map results back to business IDs
      const newResults = {};
      const newStatusMap = { ...newStatuses };

      (data.results || []).forEach((r, idx) => {
        const biz = DEFAULT_BUSINESSES.filter(b => selected.has(b.id))[idx];
        if (!biz) return;
        newResults[biz.id] = r;
        newStatusMap[biz.id] = r.status === 'success' ? 'success' : 'error';
      });

      setStatuses(newStatusMap);
      setResults(newResults);
      setBatchSummary({
        total: data.total || selected.size,
        succeeded: data.succeeded || 0,
        failed: data.failed || 0,
        mall_id: data.mall_id || mallTenantId,
      });
      setDone(true);

      if (onComplete) onComplete(data);

    } catch (err) {
      setError(err.message || 'Batch onboarding failed. Please try again.');
      // Mark all loading as error
      setStatuses(prev => {
        const next = { ...prev };
        for (const id of selected) {
          if (next[id] === 'loading') next[id] = 'error';
        }
        return next;
      });
    } finally {
      setRunning(false);
    }
  };

  const handleReset = () => {
    setStatuses(Object.fromEntries(DEFAULT_BUSINESSES.map(b => [b.id, 'idle'])));
    setResults({});
    setBatchSummary(null);
    setDone(false);
    setError('');
    setSelected(new Set(DEFAULT_BUSINESSES.map(b => b.id)));
  };

  return (
    <div style={styles.container} data-testid="batch-onboarding">
      <div style={styles.header}>
        <h3 style={styles.title}>🏪 Batch Onboard Mall Businesses</h3>
        <p style={styles.subtitle}>
          Select the businesses to onboard as sub-tenants under the Kona-Point Shopping Mall.
          5 existing demos + 6 new verticals = 11 total.
        </p>
      </div>

      {/* Controls */}
      {!done && (
        <div style={styles.controls}>
          <button
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={selectAll}
            disabled={running}
          >
            Select All
          </button>
          <button
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={deselectAll}
            disabled={running}
          >
            Deselect All
          </button>
          <span style={{ fontSize: '13px', color: '#888', marginLeft: 'auto' }}>
            {selected.size} of {DEFAULT_BUSINESSES.length} selected
          </span>
        </div>
      )}

      {/* Progress bar (shown while running) */}
      {running && (
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
        </div>
      )}

      {/* Business list */}
      <div style={styles.businessList}>
        {DEFAULT_BUSINESSES.map((biz) => {
          const isSelected = selected.has(biz.id);
          const status = statuses[biz.id] || 'idle';
          const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.idle;
          const result = results[biz.id];

          return (
            <div
              key={biz.id}
              style={{
                ...styles.businessItem,
                ...(isSelected ? styles.businessItemSelected : {}),
                ...(running ? styles.businessItemDisabled : { cursor: 'pointer' }),
              }}
              onClick={() => toggleBusiness(biz.id)}
              data-testid={`business-item-${biz.id}`}
            >
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={isSelected}
                onChange={() => toggleBusiness(biz.id)}
                disabled={running}
                aria-label={`Select ${biz.company_name}`}
                onClick={e => e.stopPropagation()}
              />
              <span style={styles.icon}>{biz.icon}</span>
              <div style={styles.businessInfo}>
                <div style={styles.businessName}>{biz.company_name}</div>
                <div style={styles.businessMeta}>
                  {biz.business_type.replace(/_/g, ' ')} &nbsp;·&nbsp; {biz.contact_email}
                </div>
                {result?.status === 'error' && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                    ⚠ {result.message}
                  </div>
                )}
                {result?.status === 'success' && result.client_id && (
                  <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>
                    client_id: {result.client_id}
                  </div>
                )}
              </div>
              <span style={biz.isExisting ? styles.existingBadge : styles.newBadge}>
                {biz.isExisting ? 'Existing' : 'New'}
              </span>
              <span style={{ ...styles.statusBadge, ...statusStyle }}>
                {statusStyle.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <div style={styles.errorDetail} role="alert">
          ⚠ {error}
        </div>
      )}

      {/* Summary panel */}
      {batchSummary && (
        <div style={styles.summary} data-testid="batch-summary">
          <div style={styles.summaryTitle}>📊 Batch Onboarding Summary</div>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryNumber, color: '#1a1a2e' }}>
                {batchSummary.total}
              </div>
              <div style={styles.summaryLabel}>Total</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryNumber, color: '#16a34a' }}>
                {batchSummary.succeeded}
              </div>
              <div style={styles.summaryLabel}>Succeeded</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryNumber, color: batchSummary.failed > 0 ? '#dc2626' : '#9ca3af' }}>
                {batchSummary.failed}
              </div>
              <div style={styles.summaryLabel}>Failed</div>
            </div>
          </div>
          {batchSummary.failed === 0 ? (
            <p style={{ fontSize: '13px', color: '#16a34a', margin: 0 }}>
              ✅ All businesses onboarded successfully!
            </p>
          ) : (
            <p style={{ fontSize: '13px', color: '#ca8a04', margin: 0 }}>
              ⚠ {batchSummary.failed} business{batchSummary.failed > 1 ? 'es' : ''} failed.
              The rest were onboarded successfully.
            </p>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {!done ? (
          <button
            style={{
              ...styles.btn,
              ...styles.btnPrimary,
              opacity: running || selected.size === 0 ? 0.6 : 1,
            }}
            onClick={handleRun}
            disabled={running || selected.size === 0}
            data-testid="run-batch-btn"
          >
            {running
              ? `⏳ Onboarding… (${completedCount}/${totalSelected})`
              : `🚀 Onboard ${selected.size} Business${selected.size !== 1 ? 'es' : ''}`}
          </button>
        ) : (
          <>
            <button
              style={{ ...styles.btn, ...styles.btnSecondary }}
              onClick={handleReset}
              data-testid="reset-batch-btn"
            >
              ↺ Reset
            </button>
            {onComplete && (
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={() => onComplete(batchSummary)}
                data-testid="continue-btn"
              >
                Continue →
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BatchOnboarding;
